// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 4eb99029-9e4b-4f78-882f-a4bebff53cfd

// Small presentational helpers shared across the CMDB screens.
import React from 'react';
import { Box, Chip, Tooltip, LinearProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { typeMeta, resolvePicklist, FIELD_TYPES } from './schema';

export function TypeBadge({ type, size = 22 }) {
  const m = typeMeta(type);
  return (
    <Tooltip title={m.label}>
      <Box
        sx={{
          width: size, height: size, minWidth: size, borderRadius: '4px',
          bgcolor: m.color, color: '#fff', fontSize: size * 0.5, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {m.badge}
      </Box>
    </Tooltip>
  );
}

const TAG_COLORS = {
  Cell: '#F8BBD0', 'DR Tier': '#C8E6C9', 'Managed By': '#B3E5FC', Other: '#E1BEE7',
};

// Some imported tag values already carry their group ("DR Tier: 2"), so blindly
// prefixing the group produced "DR Tier: DR Tier: 2".
export function tagLabel(t) {
  const value = String(t.value || '');
  if (!t.group || t.group === 'Other') return value;
  if (value.toLowerCase().startsWith(String(t.group).toLowerCase())) return value;
  return `${t.group}: ${value}`;
}

export function TagChips({ tags = [], size = 'small' }) {
  return (
    <>
      {tags.map((t, i) => (
        <Chip
          key={i}
          size={size}
          label={tagLabel(t)}
          sx={{ bgcolor: TAG_COLORS[t.group] || '#eee', color: '#1a1a1a', mr: 0.5, mb: 0.5, fontWeight: 600 }}
        />
      ))}
    </>
  );
}

// Rating: index of value in its picklist → filled stars (1..N).
export function Stars({ value, picklist }) {
  const opts = resolvePicklist(picklist);
  const n = opts.length || 4;
  const filled = Math.max(0, opts.indexOf(value) + 1);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {Array.from({ length: n }).map((_, i) =>
        i < filled ? (
          <StarIcon key={i} sx={{ fontSize: 16, color: '#F5A623' }} />
        ) : (
          <StarBorderIcon key={i} sx={{ fontSize: 16, color: '#bbb' }} />
        )
      )}
      <span style={{ marginLeft: 4 }}>{value || '—'}</span>
    </Box>
  );
}

// Business Criticality → arrows.
export function Arrows({ value, picklist }) {
  const opts = resolvePicklist(picklist);
  const n = opts.length || 4;
  const filled = Math.max(0, opts.indexOf(value) + 1);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {Array.from({ length: n }).map((_, i) => (
        <ArrowUpwardIcon key={i} sx={{ fontSize: 15, color: i < filled ? '#D0021B' : '#ccc' }} />
      ))}
      <span style={{ marginLeft: 4 }}>{value || '—'}</span>
    </Box>
  );
}

export function CompletionBar({ value = 0, width = 120 }) {
  const color = value >= 75 ? '#2E7D32' : value >= 40 ? '#F5A623' : '#D0021B';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, width }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, value)}
        sx={{ flex: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: color } }}
      />
      <span style={{ fontSize: 12, color: '#555', minWidth: 32 }}>{value}%</span>
    </Box>
  );
}

const SEAL_COLORS = { Approved: '#2E7D32', Draft: '#9E9E9E', Broken: '#D0021B', Rejected: '#D0021B' };
export function QualitySeal({ value }) {
  if (!value) return null;
  return (
    <Chip
      size="small"
      icon={value === 'Approved' ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
      label={value}
      sx={{ bgcolor: 'transparent', border: `1px solid ${SEAL_COLORS[value] || '#999'}`, color: SEAL_COLORS[value] || '#999', fontWeight: 600 }}
    />
  );
}

// LeanIX-style lifecycle timeline bar (active-from → +6y horizon).
export function LifecycleBar({ from, state }) {
  const start = new Date('2023-07-01').getTime();
  const end = new Date('2029-12-31').getTime();
  // A free-text `activeFrom` ("Q3 2024", "TBD") gives NaN here, and
  // Math.max/min propagate it — the bar emitted left: "NaN%" and silently
  // collapsed. An undatable value is unknown, not position zero, so fall back
  // to the start of the horizon and say the date could not be read.
  const parsed = from ? new Date(from).getTime() : start;
  const unreadable = from && !Number.isFinite(parsed);
  const at = Number.isFinite(parsed) ? parsed : start;
  const raw = ((at - start) / (end - start)) * 100;
  const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
  const color = state === 'End of Life' ? '#D0021B' : state === 'Phase Out' ? '#F5A623' : '#2f5fa8';
  return (
    <Box sx={{ position: 'relative', height: 26, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', left: `${pct}%`, right: 0, top: 0, bottom: 0, bgcolor: color, opacity: 0.85 }} />
      <Box sx={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, width: 2, bgcolor: 'sectionHeader.main' }} />
      <Box sx={{ position: 'absolute', left: `${pct}%`, top: 2, ml: 0.5, fontSize: 11, color: '#fff', fontWeight: 600 }}>
        {state} {from ? `· ${from}` : ''}{unreadable ? ' (date not recognised)' : ''}
      </Box>
    </Box>
  );
}

// Render a single field value read-only.
export function fieldDisplay(field, value) {
  if (value === undefined || value === null || value === '') return <span style={{ color: '#aaa' }}>—</span>;
  switch (field.type) {
    case FIELD_TYPES.RATING:
      return <Stars value={value} picklist={field.picklist} />;
    case FIELD_TYPES.ARROWS:
      return <Arrows value={value} picklist={field.picklist} />;
    case FIELD_TYPES.BOOLEAN:
      return value ? 'Yes' : 'No';
    case FIELD_TYPES.CURRENCY: {
      // Imports carry "$1.2M" or "TBD"; Number() turns those into NaN and the
      // fact sheet printed "$ NaN" to the client. Show what was actually
      // captured instead of a broken number. Blank is already handled above.
      const n = Number(value);
      return Number.isFinite(n) ? `$ ${n.toLocaleString()}` : String(value);
    }
    case FIELD_TYPES.PERCENT:
      return `${value}%`;
    case FIELD_TYPES.URL:
      return (
        <a href={value} target="_blank" rel="noreferrer" style={{ color: '#0B5FFF' }}>
          {value}
        </a>
      );
    default:
      return String(value);
  }
}
